import {
  Badge,
  FlexColumn,
  FlexRow,
  formatHash,
  HashLength,
  TruncateBox,
} from '@make-software/cspr-ui';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@block-explorer/ui';
import React from 'react';
import styled from 'styled-components';
import Avatar from '../avatar/avatar';
import TooltipWithExtendedInfo from '../tooltip-with-extended-info/tooltip-with-extended-info';
import CopyButton from '../copy-button/copy-button';
import { isValidPublicKey } from '../../utils/account.tsx';

const BadgeContainer = styled(FlexRow)(() => ({
  '&&&': {
    height: '17px',
  },
}));

const StyledTruncateBox = styled(TruncateBox)(() => ({
  height: '20px',
}));

interface AccountInfoDataWithBadgeProps {
  loading: boolean;
  logo: string | undefined | null;
  name: string | undefined;
  publicKey: string | null | undefined;
  csprName?: string | undefined;
  hashLength?: HashLength;
  badgeLabel?: string | null;
  isCopyEnabled?: boolean;
}

const AccountInfoDataWithBadge = ({
  publicKey,
  csprName,
  logo,
  name,
  loading,
  hashLength,
  badgeLabel,
  isCopyEnabled = false,
}: AccountInfoDataWithBadgeProps) => {
  const { t } = useTranslation();
  if (loading || !publicKey) {
    return (
      <FlexRow align="center" itemsSpacing={12}>
        <Avatar hash={publicKey} loading={loading} size={'small'} />
      </FlexRow>
    );
  }

  const csprNameOrPublicKey = csprName || publicKey;

  return (
    <FlexRow align="center" itemsSpacing={12}>
      {logo ? (
        <Avatar
          src={logo}
          loading={loading}
          size={'small'}
          alt={'Account logo'}
        />
      ) : (
        <Avatar hash={publicKey} loading={loading} size={'small'} />
      )}

      <TooltipWithExtendedInfo
        extendedLine={{ title: csprName, caption: t('CSPR.name') }}
        publicKey={publicKey}
      >
        <FlexColumn>
          {name ? (
            <>
              <FlexRow itemsSpacing={4} align={'baseline'}>
                <BodyText size={3} monotype={!csprName} variation={'black'}>
                  {isValidPublicKey(csprNameOrPublicKey)
                    ? formatHash(publicKey, hashLength)
                    : csprName}
                </BodyText>
                {isCopyEnabled ? (
                  <CopyButton value={csprNameOrPublicKey} type={'full'} />
                ) : null}
              </FlexRow>
              <FlexRow itemsSpacing={6} align={'center'}>
                <FlexRow>
                  <StyledTruncateBox size={5}>
                    <BodyText size={3} variation="darkGray" noWrap>
                      {name}
                    </BodyText>
                  </StyledTruncateBox>
                </FlexRow>
                {badgeLabel ? (
                  <BadgeContainer>
                    <Badge
                      variation={'green'}
                      label={t(badgeLabel)}
                      lineHeight={'xxs'}
                    />
                  </BadgeContainer>
                ) : null}
              </FlexRow>
            </>
          ) : (
            <>
              <FlexRow itemsSpacing={4} align={'baseline'}>
                <BodyText size={3} variation={'black'} monotype={!csprName}>
                  {isValidPublicKey(csprNameOrPublicKey)
                    ? formatHash(publicKey, hashLength)
                    : csprName}
                </BodyText>
                {isCopyEnabled ? (
                  <CopyButton value={csprNameOrPublicKey} type={'full'} />
                ) : null}
              </FlexRow>
              {badgeLabel ? (
                <BadgeContainer>
                  <Badge
                    variation={'green'}
                    label={badgeLabel}
                    lineHeight={'xxs'}
                  />
                </BadgeContainer>
              ) : null}
            </>
          )}
        </FlexColumn>
      </TooltipWithExtendedInfo>
    </FlexRow>
  );
};

export default AccountInfoDataWithBadge;
